import { lazy, Suspense } from 'react';
import { Navigate, Outlet, Route, Routes } from 'react-router';
import { GlassFilters, Spinner } from '../ui';
import { AcceptInvitationPage } from './auth/AcceptInvitationPage';
import { ForgotPasswordPage } from './auth/ForgotPasswordPage';
import { LoginPage } from './auth/LoginPage';
import { RegisterPage } from './auth/RegisterPage';
import { RequireAdmin } from './auth/RequireAdmin';
import { RequireAuth } from './auth/RequireAuth';
import { RequireFlag } from './auth/RequireFlag';
import { ResetPasswordPage } from './auth/ResetPasswordPage';
import { DashboardRedirect, StartPage } from './fulfillment/dashboard/DashboardPage';
import { NotFound } from './NotFound';
import { RecordListPage } from './records/RecordListPage';
import { ReportsScreen } from './reports/ReportsScreen';
import { AppShell } from './shell/AppShell';
import { DataPage } from './shell/DataPage';

/**
 * The route table. Everything behind the guard shares the AppShell; login, register and
 * the kitchen sink stand alone - the first two because a nav bar on a credential screen is
 * pointless, the last because it must be viewable without a session.
 *
 * `BrowserRouter` is mounted in main.tsx, which keeps this component testable under a
 * MemoryRouter.
 *
 * WHAT IS LAZY, AND WHY. The list screen and the shell are what a user lands on, so they
 * stay in the entry chunk. Everything else is either a route most sessions never open (the
 * admin area, the people screen), one nobody opens on a phone on a train (the kitchen
 * sink), or the record page - which pulls in the entire field-INPUT layer that a list does
 * not need. One `Suspense` boundary around the table rather than one per route: a spinner
 * per route is more moving parts for the same flash.
 */
const RecordPage = lazy(async () => ({
  default: (await import('./record/RecordPage')).RecordPage,
}));
const ObjectAdminPage = lazy(async () => ({
  default: (await import('./admin/ObjectAdminPage')).ObjectAdminPage,
}));
const ObjectDetailPage = lazy(async () => ({
  default: (await import('./admin/ObjectDetailPage')).ObjectDetailPage,
}));
const WebhookAdminPage = lazy(async () => ({
  default: (await import('./admin/WebhookAdminPage')).WebhookAdminPage,
}));
const WebhookDetailPage = lazy(async () => ({
  default: (await import('./admin/WebhookDetailPage')).WebhookDetailPage,
}));
const MembersPage = lazy(async () => ({
  default: (await import('./members/MembersPage')).MembersPage,
}));
const RoleDetailPage = lazy(async () => ({
  default: (await import('./members/RoleDetailPage')).RoleDetailPage,
}));
const IdentityAdminPage = lazy(async () => ({
  default: (await import('./admin/IdentityAdminPage')).IdentityAdminPage,
}));
/** The admin area's three new screens (admin-area.md D16, D20, D24). Lazy like the rest. */
const AgentAdminPage = lazy(async () => ({
  default: (await import('./admin/AgentAdminPage')).AgentAdminPage,
}));
const InstanceAdminPage = lazy(async () => ({
  default: (await import('./admin/InstanceAdminPage')).InstanceAdminPage,
}));
/**
 * The workflow builder (workflow-builder.md D37, D38). Lazy like every other admin screen:
 * it pulls in the filter editor and the whole value-slot layer, which a list does not need,
 * and most sessions never open it.
 */
const WorkflowAdminPage = lazy(async () => ({
  default: (await import('./workflows/WorkflowAdminPage')).WorkflowAdminPage,
}));
const WorkflowDetailPage = lazy(async () => ({
  default: (await import('./workflows/WorkflowDetailPage')).WorkflowDetailPage,
}));
/**
 * The one PERSONAL surface in the product (notes-tasks-comments.md 12.2). Lazy like the
 * rest: it is a screen people open deliberately, not one they land on.
 */
const TasksPage = lazy(async () => ({
  default: (await import('./tasks/TasksPage')).TasksPage,
}));
/** The object-level possible-duplicates list: a screen somebody works deliberately. */
const DuplicatesPage = lazy(async () => ({
  default: (await import('./identity/DuplicatesPage')).DuplicatesPage,
}));
/**
 * CSV import and export (csv-import-export.md section 12). Lazy for the reason every
 * other secondary screen is: a wizard is a thing somebody opens deliberately, and the
 * import half pulls in the whole column-format layer that no list needs.
 */
const ImportsPage = lazy(async () => ({
  default: (await import('./csv/ImportsPage')).ImportsPage,
}));
const ImportWizardPage = lazy(async () => ({
  default: (await import('./csv/ImportWizardPage')).ImportWizardPage,
}));
const ExportsPage = lazy(async () => ({
  default: (await import('./csv/ExportsPage')).ExportsPage,
}));
/**
 * INBOUND INTAKE (`inbound-intake.md` D19). Lazy like every other settings screen: it is a
 * screen an operator opens deliberately, and the detail page pulls in the mapping editor and
 * the dry-run panel that no list needs.
 */
const HealthSettingsPage = lazy(async () => ({
  default: (await import('./settings/health/HealthSettingsPage')).HealthSettingsPage,
}));
const InboundSourcesPage = lazy(async () => ({
  default: (await import('./intake/InboundSourcesPage')).InboundSourcesPage,
}));
const InboundSourceDetailPage = lazy(async () => ({
  default: (await import('./intake/InboundSourceDetailPage')).InboundSourceDetailPage,
}));
/**
 * THE FULFILLMENT SECTION (fulfillment COMMON section 4): five pages the page packages build
 * and two settings panes the shell package builds. Lazy like every other secondary screen. Until
 * a page file exists in the tree its route draws `Placeholder`, which the orchestrator swaps for
 * the real import at integration.
 */
const FulfillmentOnboardingPage = lazy(() => import('./fulfillment/onboarding/OnboardingPage'));
const FulfillmentClientsPage = lazy(() => import('./fulfillment/clients/ClientsPage'));
const FulfillmentClientPage = lazy(() => import('./fulfillment/clients/ClientPage'));
const FulfillmentIntakePage = lazy(() => import('./fulfillment/intake/IntakePage'));
const FulfillmentIntakesPage = lazy(() => import('./fulfillment/intake/IntakesPage'));
/** The bot's pane (register A8-3), whose first section is the welcome messages. */
const BotSettingsPage = lazy(async () => ({
  default: (await import('./settings/WelcomeMessagesPage')).BotSettingsPage,
}));
const SidebarSettingsPage = lazy(async () => ({
  default: (await import('./settings/SidebarSettingsPage')).SidebarSettingsPage,
}));
const KitchenSink = lazy(async () => ({
  default: (await import('./dev/KitchenSink')).KitchenSink,
}));
const ShellStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/ShellStates')).ShellStates }))
    : null;
/** P5-7, the record's own state gallery. Same gate as the shell's: dev or fixtures. */
const RecordStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/RecordStates')).RecordStates }))
    : null;
/** P4-8: the list screen's parts with their origins. Same gate as the shell's page. */
const ListStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/ListStates')).ListStates }))
    : null;
/** E4: the checkbox primitive's states, on the same dev-or-fixtures gate as the shell gallery. */
const CheckboxStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/CheckboxStates')).CheckboxStates }))
    : null;
/** M2: the same dev-or-fixtures boundary as the checkbox gallery. */
const MotionStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/MotionStates')).MotionStates }))
    : null;
/** E1: the tasks screen's five states, on the same dev-or-fixtures gate as the shell gallery. */
const TasksStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/TasksStates')).TasksStates }))
    : null;
/** C4b: the pending-changes states, on the same dev-or-fixtures gate as the shell gallery. */
const PendingStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/PendingStates')).PendingStates }))
    : null;
/** E2: the reports screen's states, on the same dev-or-fixtures gate as the shell gallery. */
const ReportsStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/ReportsStates')).ReportsStates }))
    : null;
/** E3: the credential screens' states, on the same dev-or-fixtures gate as the shell gallery. */
const AuthStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/AuthStates')).AuthStates }))
    : null;
/** FE-FOUNDATION: the redesign's shared parts in every state, on the same dev-or-fixtures gate. */
const RedesignStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/RedesignStates')).RedesignStates }))
    : null;
/** P7-5: the settings pane's pieces, on the same dev-or-fixtures gate as the shell gallery. */
const SettingsStates =
  import.meta.env.DEV ||
  (import.meta.env as { readonly VITE_FIXTURES?: string }).VITE_FIXTURES === '1'
    ? lazy(async () => ({ default: (await import('./dev/SettingsStates')).SettingsStates }))
    : null;
/**
 * THE LANDING SCREEN, and therefore NOT lazy (dashboard.md D16, D17). Every session
 * starts here - which is also the single most reliable discovery mechanism a screen can
 * have, and the reason the mobile tab bar stays at six.
 */

function RouteFallback() {
  return (
    <div className="flex min-h-64 items-center justify-center py-12" aria-busy="true">
      <Spinner label="Loading" />
    </div>
  );
}

export function App() {
  return (
    <Suspense fallback={<RouteFallback />}>
      {/*
        THE GLASS LENSES, mounted once for the whole product and above the route table on
        purpose: the login screen and the two dev galleries render chrome too, and a filter
        that lives inside a route would be gone from the document the moment that route
        unmounted - taking the material off every surface pointing at it (`ui/glass.css`).
      */}
      <GlassFilters />
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/register" element={<RegisterPage />} />
        {/*
          accounts F1: the three screens somebody uses while they have no session. They
          stand beside `/login` rather than inside the guard for the reason the guard
          exists - a person holding an invitation has no account to be signed in to, and
          a person who forgot their password cannot be asked to sign in first.
        */}
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
        <Route path="/invite/:token" element={<AcceptInvitationPage />} />
        <Route path="/dev/kitchen-sink" element={<KitchenSink />} />
        {ShellStates !== null && <Route path="/dev/shell-states" element={<ShellStates />} />}
        {RecordStates !== null && <Route path="/dev/record-states" element={<RecordStates />} />}
        {ListStates !== null && <Route path="/dev/list-states" element={<ListStates />} />}
        {SettingsStates !== null && (
          <Route path="/dev/settings-states" element={<SettingsStates />} />
        )}
        {CheckboxStates !== null && (
          <Route path="/dev/checkbox-states" element={<CheckboxStates />} />
        )}
        {MotionStates !== null && <Route path="/dev/motion-states" element={<MotionStates />} />}
        {TasksStates !== null && <Route path="/dev/tasks-states" element={<TasksStates />} />}
        {PendingStates !== null && <Route path="/dev/pending-states" element={<PendingStates />} />}
        {ReportsStates !== null && <Route path="/dev/reports-states" element={<ReportsStates />} />}
        {AuthStates !== null && <Route path="/dev/auth-states" element={<AuthStates />} />}
        {RedesignStates !== null && (
          <Route path="/dev/redesign-states" element={<RedesignStates />} />
        )}

        <Route
          element={
            <RequireAuth>
              <AppShell />
            </RequireAuth>
          }
        >
          {/*
            THE DASHBOARD IS THE START PAGE (redesign register H1): Home is gone, and the
            index renders the fulfillment Dashboard wherever its model resolves. `HomeRedirect` is NOT
            gone: it is the fallback branch `StartPage` renders on an installation without the
            fulfillment objects, exactly as Home did, so the platform stays generic.
          */}
          <Route index element={<StartPage />} />
          {/*
            THE PHONE'S DATA TAB IS A ROOT SCREEN (round 3, finding A5). It was a bottom
            sheet, which a tab cannot be: `spec/anatomy/IOS.md` makes every tab the root of
            its own navigation stack, and a record pushed from a modal sheet has nowhere to
            push to. The desktop reaches the same list in the sidebar and never needs this
            route, but it renders there too rather than 404-ing a URL somebody sends on.
          */}
          <Route path="data" element={<DataPage />} />
          <Route path="objects/:objectId" element={<RecordListPage />} />
          <Route path="objects/:objectId/duplicates" element={<DuplicatesPage />} />
          <Route path="objects/:objectId/records/:recordId" element={<RecordPage />} />
          {/*
              REPORTS (`reports.md` T7, composition spec S1). Two routes: the list and one
              report. Both are inside the auth guard and neither is behind a flag - T6 gates
              WRITING, and a member who may not build a report still reads every one.
            */}
          <Route path="reports" element={<ReportsScreen />} />
          <Route path="reports/:reportId" element={<ReportsScreen />} />
          {/* Sent links and the sidebar's Dashboard row keep working (H1): back to `/`. */}
          <Route path="fulfillment/dashboard" element={<DashboardRedirect />} />
          <Route path="fulfillment/onboarding" element={<FulfillmentOnboardingPage />} />
          <Route path="fulfillment/clients" element={<FulfillmentClientsPage />} />
          <Route path="fulfillment/clients/:agencyId" element={<FulfillmentClientPage />} />
          <Route path="fulfillment/intakes" element={<FulfillmentIntakesPage />} />
          <Route path="fulfillment/intakes/:submissionId" element={<FulfillmentIntakePage />} />
          <Route path="tasks" element={<TasksPage />} />
          {/*
            THE REVIEW QUEUE IS A SEGMENT OF TASKS (E1; `crm-design/spec/screens/tasks.md`
            section 8). The route stays - the sidebar's own Pending changes row, every link
            somebody has sent and `pending.spec.ts` all point at it - and it renders the same
            screen with its third segment selected rather than a page of its own.
          */}
          <Route path="pending" element={<TasksPage initialSegment="review" />} />
          {/**
           * `/settings` was a 404 (admin-area.md D1): the children were registered and
           * then `*` fell through to NotFound, so a user who trimmed the URL landed on
           * "not found" inside a section that plainly exists.
           */}
          <Route path="settings" element={<Navigate to="/settings/objects" replace />} />
          {/**
           * OPEN TO MEMBERS, as an explicit READ-ONLY view (D2). The server keeps
           * metadata READS open to any authenticated caller and gates only mutations, and
           * hiding these two would hide the only place the meaning of a field is written
           * down. The screens disable their own controls instead.
           */}
          {/* The person's own pane and the welcome texts: no flag, no admin (fulfillment P2). */}
          <Route path="settings/sidebar" element={<SidebarSettingsPage />} />
          {/* The Appearance pane is gone (register A8-7): its settings are account menu submenus. */}
          <Route path="settings/appearance" element={<Navigate to="/settings" replace />} />
          <Route path="settings/bot" element={<BotSettingsPage />} />
          {/* Every link to the welcome messages' own pane lands on their section (A8-3). */}
          <Route
            path="settings/welcome-messages"
            element={<Navigate to="/settings/bot" replace />}
          />
          <Route path="settings/objects" element={<ObjectAdminPage />} />
          <Route path="settings/objects/:objectId" element={<ObjectDetailPage />} />
          {/**
           * THE CSV SCREENS ARE FLAG-GATED, NOT ADMIN-GATED (csv-import-export.md D33).
           *
           * `IMPORT_CSV` and `EXPORT_CSV` are seeded to `Operator` and `Owner`, and an
           * instance can grant either onward to a role that is not an administrator - so
           * `RequireAdmin`, which reads the legacy derived `UserRole`, would answer the
           * wrong question in both directions. `RequireFlag` asks the caller's own
           * permission union, which is the same thing the server asks.
           */}
          <Route
            path="settings/imports"
            element={
              <RequireFlag flag="IMPORT_CSV">
                <ImportsPage />
              </RequireFlag>
            }
          />
          <Route
            path="settings/imports/:runId"
            element={
              <RequireFlag flag="IMPORT_CSV">
                <ImportWizardPage />
              </RequireFlag>
            }
          />
          <Route
            path="settings/exports"
            element={
              <RequireFlag flag="EXPORT_CSV">
                <ExportsPage />
              </RequireFlag>
            }
          />
          {/**
           * INBOUND SOURCES ARE FLAG-GATED, NOT ADMIN-GATED (`inbound-intake.md` D2, D19),
           * for the reason the CSV screens are: `INTAKE_MANAGE` is held by Operator and Owner
           * through their own grants and an instance may grant it onward to a role that is
           * not an administrator, so `RequireAdmin` - which reads the legacy derived
           * `UserRole` - would answer the wrong question in both directions. `RequireFlag`
           * asks the caller's own permission union, which is what the server asks.
           */}
          <Route
            path="settings/inbound"
            element={
              <RequireFlag flag="INTAKE_MANAGE">
                <InboundSourcesPage />
              </RequireFlag>
            }
          />
          <Route
            path="settings/inbound/:sourceId"
            element={
              <RequireFlag flag="INTAKE_MANAGE">
                <InboundSourceDetailPage />
              </RequireFlag>
            }
          />
          {/**
           * HEALTH RULES ARE FLAG-GATED, NOT ADMIN-GATED (`phase-b-coach.md` D12), for the
           * reason the inbound screens above are: `HEALTH_MANAGE` is held by Owner and
           * Operator through their own grants and an instance may grant it onward to a role
           * that is not an administrator, so `RequireAdmin` - which reads the legacy derived
           * `UserRole` - would answer the wrong question in both directions.
           */}
          <Route
            path="settings/health"
            element={
              <RequireFlag flag="HEALTH_MANAGE">
                <HealthSettingsPage />
              </RequireFlag>
            }
          />
          {/**
           * Everything else under Settings is admin-only, in ONE place rather than six
           * in-component branches (D2).
           */}
          <Route
            element={
              <RequireAdmin>
                <Outlet />
              </RequireAdmin>
            }
          >
            <Route path="settings/workflows" element={<WorkflowAdminPage />} />
            <Route path="settings/workflows/:workflowId" element={<WorkflowDetailPage />} />
            <Route path="settings/webhooks" element={<WebhookAdminPage />} />
            <Route path="settings/webhooks/:webhookId" element={<WebhookDetailPage />} />
            {/**
             * THE IA RECONCILIATION (roles-and-permissions.md section 6).
             *
             * `settings/users` is REPLACED by `settings/members` and `settings/keys` is
             * ABSORBED into it, "because a key is a principal holding roles, and splitting
             * principals across two screens is what makes who-can-do-X hard to answer".
             *
             * BOTH OLD PATHS REDIRECT rather than 404. They are in muscle memory, in the
             * sidebar of an open tab and in at least one e2e spec, and a 404 for a screen
             * that plainly still exists is the failure `admin-area.md` D1 already fixed once
             * for `/settings` itself.
             */}
            <Route path="settings/members" element={<MembersPage />} />
            <Route path="settings/members/roles" element={<MembersPage tab="roles" />} />
            <Route path="settings/members/roles/:roleId" element={<RoleDetailPage />} />
            <Route path="settings/users" element={<Navigate to="/settings/members" replace />} />
            <Route path="settings/keys" element={<Navigate to="/settings/members" replace />} />
            <Route path="settings/identity" element={<IdentityAdminPage />} />
            <Route path="settings/agent" element={<AgentAdminPage />} />
            <Route path="settings/instance" element={<InstanceAdminPage />} />
          </Route>
          <Route path="*" element={<NotFound />} />
        </Route>
      </Routes>
    </Suspense>
  );
}
